"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/layout/Navbar";
import { usePageTitle } from "@/lib/usePageTitle";
import type { Id } from "../../../../convex/_generated/dataModel";

export default function AdminBillingPage() {
  usePageTitle("Admin: Billing");

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<Id<"users"> | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);

  const searchResults = useQuery(
    api.admin.searchUsers,
    activeSearch ? { searchTerm: activeSearch } : "skip"
  );

  const userDetail = useQuery(
    api.admin.getUserDetail,
    selectedUserId ? { userId: selectedUserId } : "skip"
  );

  const resetBilling = useMutation(api.admin.resetUserBilling);

  const handleSearch = () => {
    setActiveSearch(searchTerm.trim());
    setSelectedUserId(null);
    setActionResult(null);
  };

  const handleResetBilling = async () => {
    if (!selectedUserId) return;
    try {
      await resetBilling({ userId: selectedUserId });
      setActionResult("Billing reset to free/none successfully.");
    } catch (err) {
      setActionResult(err instanceof Error ? err.message : "Failed to reset billing.");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex-1 px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-5xl space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Admin: Billing</h1>
              <p className="text-sm text-muted">Search users, view subscriptions, manage billing</p>
            </div>
            <Link to="/dashboard" className="text-sm text-primary hover:underline">
              Dashboard
            </Link>
          </div>

          {/* Search */}
          <div className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Search by email or Clerk ID..."
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <Button onClick={handleSearch}>Search</Button>
          </div>

          {actionResult && (
            <div className="rounded-md border border-border bg-foreground/5 p-3 text-sm">
              {actionResult}
            </div>
          )}

          {/* Results */}
          {searchResults && searchResults.length > 0 && (
            <div className="rounded-lg border border-border bg-background p-4">
              <h2 className="mb-3 text-sm font-semibold">Results</h2>
              {searchResults.map((user) => (
                <button
                  key={user._id}
                  type="button"
                  onClick={() => {
                    setSelectedUserId(user._id);
                    setActionResult(null);
                  }}
                  className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${
                    selectedUserId === user._id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="font-medium">{user.email}</div>
                  <div className="text-muted">
                    {user.clerkId} | Tier: {user.subscriptionTier} | Status: {user.subscriptionStatus} | Admin: {user.isAdmin ? "Yes" : "No"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {activeSearch && searchResults && searchResults.length === 0 && (
            <div className="text-sm text-muted">No users found.</div>
          )}

          {/* User Details */}
          {userDetail && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-background p-4">
                <h2 className="mb-2 text-sm font-semibold">User Details</h2>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Email: <span className="font-medium">{userDetail.user.email}</span></div>
                  <div>Clerk ID: <span className="font-medium">{userDetail.user.clerkId}</span></div>
                  <div>Tier: <span className="font-medium">{userDetail.user.subscriptionTier}</span></div>
                  <div>Status: <span className="font-medium">{userDetail.user.subscriptionStatus}</span></div>
                  <div>Admin: <span className="font-medium">{userDetail.user.isAdmin ? "Yes" : "No"}</span></div>
                  <div>Trial Used: <span className="font-medium">{userDetail.user.hasUsedTrial ? "Yes" : "No"}</span></div>
                  <div>Subscription ID: <span className="font-medium">{userDetail.user.billingPolarSubscriptionId ?? "—"}</span></div>
                  <div>Customer ID: <span className="font-medium">{userDetail.user.polarCustomerId ?? "—"}</span></div>
                  {userDetail.user.billingCurrentPeriodEnd && (
                    <div>Period End: <span className="font-medium">{new Date(userDetail.user.billingCurrentPeriodEnd).toLocaleDateString()}</span></div>
                  )}
                  {userDetail.user.trialEndsAt && (
                    <div>Trial Ends: <span className="font-medium">{new Date(userDetail.user.trialEndsAt).toLocaleDateString()}</span></div>
                  )}
                </div>
                <div className="mt-4">
                  <Button variant="destructive" size="sm" onClick={handleResetBilling}>
                    Reset Billing to Free
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-background p-4">
                <h2 className="mb-2 text-sm font-semibold">
                  Organizations ({userDetail.organizations.length})
                </h2>
                {userDetail.organizations.length === 0 ? (
                  <div className="text-sm text-muted">No organizations</div>
                ) : (
                  <div className="space-y-1">
                    {userDetail.organizations.map((org) => (
                      <div key={org._id} className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{org.name}</span>
                        <span className="text-muted">({org.slug})</span>
                        {org.archivedAt && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                            Archived
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
